// Purpose: Seed newly enabled Power Ups on an existing demo event.
// Features: Reuses Experience Engine pipeline steps, detects prior demo data,
// and maps payment Power Ups to their related Sponsor or Vendor content.
// Dependencies: Firebase client Firestore, industry manifests, and pipeline steps.
// Important: Callers must activate the Power Up before this service runs so
// Firestore rules permit collection reads and writes.

import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";
import { buildDemoConfig } from "../config/demo_defaults";
import { getIndustryTemplate } from "../industries";
import { runDemoPipeline } from "../pipeline/pipeline";
import { createAuctionStep } from "../pipeline/steps/auction_step";
import { createPassportStep } from "../pipeline/steps/passport_step";
import { createSocialStep } from "../pipeline/steps/social_step";
import { createSponsorStep } from "../pipeline/steps/sponsor_step";
import { createStoreStep } from "../pipeline/steps/store_step";
import { createTicketStep } from "../pipeline/steps/ticket_step";
import { createVendorStep } from "../pipeline/steps/vendor_step";

const POWER_UP_DEFINITIONS = [
  {
    key: "sponsors",
    activationKeys: ["sponsors", "sponsorPayments"],
    collectionName: "sponsors",
    createStep: createSponsorStep,
  },
  {
    key: "vendors",
    activationKeys: ["vendors", "vendorPayments"],
    collectionName: "vendors",
    createStep: createVendorStep,
  },
  { key: "tickets", collectionName: "tickets", createStep: createTicketStep },
  { key: "auction", collectionName: "auctionItems", createStep: createAuctionStep },
  {
    key: "passport",
    collectionName: "engagement_activities",
    createStep: createPassportStep,
  },
  { key: "store", collectionName: "store_items", createStep: createStoreStep },
  {
    key: "social",
    collectionName: "promotion_posts",
    collectionScope: "root",
    createStep: createSocialStep,
  },
];

export const getNewlyEnabledDemoPowerUps = ({ previous = {}, next = {} } = {}) =>
  POWER_UP_DEFINITIONS.filter(
    ({ key, activationKeys = [key] }) =>
      activationKeys.some(
        (activationKey) =>
          next?.[activationKey] === true && previous?.[activationKey] !== true
      )
  ).map(({ key }) => key);

const hasSeededDemoContent = async ({ eventId, collectionName, collectionScope = "event" }) => {
  const collectionRef = collectionScope === "root"
    ? collection(db, collectionName)
    : collection(db, "events", eventId, collectionName);
  const constraints = [where("demoSeed", "==", true), limit(1)];
  if (collectionScope === "root") constraints.unshift(where("eventId", "==", eventId));
  const snapshot = await getDocs(
    query(collectionRef, ...constraints)
  );

  return !snapshot.empty;
};

export const seedExistingDemoPowerUps = async ({
  eventId,
  powerUpKeys = [],
  user,
} = {}) => {
  if (!eventId || !user?.uid || powerUpKeys.length === 0) {
    return { seeded: [], alreadySeeded: [], processed: [] };
  }

  const eventRef = doc(db, "events", eventId);
  const eventSnapshot = await getDoc(eventRef);

  if (!eventSnapshot.exists()) {
    throw new Error("The demo event could not be found for Power Up seeding.");
  }

  const eventDoc = eventSnapshot.data();

  if (eventDoc.demo !== true) {
    return { seeded: [], alreadySeeded: [], processed: [] };
  }

  const requestedDefinitions = POWER_UP_DEFINITIONS.filter(({ key }) =>
    powerUpKeys.includes(key)
  );
  const alreadySeeded = [];
  const definitionsToSeed = [];

  for (const definition of requestedDefinitions) {
    if (eventDoc.demoSeededPowerUps?.[definition.key]) {
      alreadySeeded.push(definition.key);
      continue;
    }

    const contentExists = await hasSeededDemoContent({
      eventId,
      collectionName: definition.collectionName,
      collectionScope: definition.collectionScope,
    });

    if (contentExists) {
      alreadySeeded.push(definition.key);
    } else {
      definitionsToSeed.push(definition);
    }
  }

  const industryKey = eventDoc.demoIndustry || eventDoc.eventType || "car_show";
  const industryTemplate = getIndustryTemplate(industryKey);

  if (!industryTemplate) {
    throw new Error(
      `The Experience Engine does not have an Event Type template for "${industryKey}".`
    );
  }

  if (definitionsToSeed.length > 0) {
    const needsExistingPassportContext = definitionsToSeed.some(
      ({ key }) => key === "social"
    );
    const existingPassportStops = needsExistingPassportContext
      ? (await getDocs(collection(db, "events", eventId, "engagement_activities"))).docs.map(
        (snapshot) => ({ id: snapshot.id, ...snapshot.data() })
      )
      : [];
    const addOns = POWER_UP_DEFINITIONS.reduce(
      (selection, { key }) => ({
        ...selection,
        [key]: powerUpKeys.includes(key),
      }),
      {}
    );
    const demoConfig = buildDemoConfig({
      industry: industryKey,
      intent: eventDoc.demoIntent || "internal_testing",
      size: eventDoc.demoSize || "small",
      addOns,
    });

    await runDemoPipeline({
      context: {
        user,
        demoConfig,
        industryTemplate,
        passport: { stops: existingPassportStops },
        eventResult: {
          eventId,
          eventRef,
          eventDoc,
          name: eventDoc.name || "Demo Event",
          eventName: eventDoc.name || "Demo Event",
          slug: eventDoc.slug || "",
          eventSlug: eventDoc.slug || "",
        },
      },
      steps: definitionsToSeed.map(({ createStep }) => createStep()),
    });
  }

  const seeded = definitionsToSeed.map(({ key }) => key);

  return {
    seeded,
    alreadySeeded,
    processed: [...alreadySeeded, ...seeded],
  };
};
